/**
 * AppointmentPrepPage - Guided appointment preparation flow
 * 
 * Helps users prepare for doctor appointments by:
 * - Selecting/entering the doctor
 * - Recording symptoms and changes
 * - Adding questions they want to ask
 * - Selecting relevant medications, allergies, conditions
 * - Creating a concise appointment summary
 * - Marking questions as answered
 * - Saving follow-up instructions
 */

import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { 
  ArrowLeft,
  ArrowRight,
  Calendar,
  User,
  Stethoscope,
  Thermometer,
  MessageSquareText,
  Pill,
  AlertTriangle,
  Activity,
  FileText,
  CheckCircle2,
  Circle,
  Plus,
  Trash2,
  Save,
  Loader2,
  Sparkles,
  ClipboardCheck,
  ChevronDown,
  ChevronUp
} from 'lucide-react';
import { toast } from 'sonner';
import { useDemo } from '@/contexts/DemoContext';
import useSharedMedicalData from '@/hooks/useSharedMedicalData';
import API from '@/utils/api';
import { format } from 'date-fns';

const THEME = {
  bgGradient: 'linear-gradient(135deg, #F0FDF4 0%, #ECFDF5 30%, #F0FDFA 60%, #F5F3FF 100%)',
  accent: '#059669',
  accentLight: '#D1FAE5',
};

// Preparation steps
const STEPS = [
  { id: 'basics', title: 'Appointment Details', icon: Calendar },
  { id: 'concerns', title: 'Symptoms & Concerns', icon: Thermometer },
  { id: 'questions', title: 'Questions', icon: MessageSquareText },
  { id: 'health', title: 'Health Info', icon: Activity },
  { id: 'summary', title: 'Summary', icon: ClipboardCheck },
];

const AppointmentPrepPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isDemoMode, demoData } = useDemo();
  const { 
    medications, 
    allergies, 
    conditions, 
    doctors,
    loading: medicalLoading 
  } = useSharedMedicalData();

  // Get prep ID from URL for editing
  const editId = searchParams.get('id');
  const isEditing = !!editId;

  // Current step
  const [currentStep, setCurrentStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [expandedSections, setExpandedSections] = useState({});
  const [hasSubmitted, setHasSubmitted] = useState(false);

  // Form state
  const [prepData, setPrepData] = useState({
    // Basics
    id: null,
    doctorId: '',
    doctorName: '',
    specialty: '',
    appointmentDate: format(new Date(), 'yyyy-MM-dd'),
    appointmentTime: '',
    reason: '',
    
    // Concerns
    symptoms: [],
    newSymptom: '',
    symptomStart: '',
    changesSinceLastVisit: '',
    
    // Questions
    questions: [],
    newQuestion: '',
    
    // Health info selections
    selectedMedications: [],
    selectedAllergies: [],
    selectedConditions: [],
    
    // Documents
    documentsToShare: [],
    
    // Follow-up
    followUpNotes: '',
    nextSteps: '',
    
    // Status
    status: 'draft',
  });

  // Load existing preparation if editing
  useEffect(() => {
    const loadPreparation = async () => {
      if (!editId || isDemoMode) return;
      
      setLoading(true);
      try {
        const response = await API.get(`/medical/appointment-preps/${editId}`);
        const data = response.data;
        setPrepData({
          id: data.id,
          doctorId: data.doctorId || '',
          doctorName: data.doctorName || '',
          specialty: data.specialty || '',
          appointmentDate: data.appointmentDate || format(new Date(), 'yyyy-MM-dd'),
          appointmentTime: data.appointmentTime || '',
          reason: data.reason || '',
          symptoms: data.symptoms || [],
          newSymptom: '',
          symptomStart: '',
          changesSinceLastVisit: data.changesSinceLastVisit || '',
          questions: data.questions || [],
          newQuestion: '',
          selectedMedications: data.selectedMedications || [],
          selectedAllergies: data.selectedAllergies || [],
          selectedConditions: data.selectedConditions || [],
          documentsToShare: data.documentsToShare || [],
          followUpNotes: data.followUpNotes || '',
          nextSteps: data.nextSteps || '',
          status: data.status || 'draft',
        });
      } catch (error) {
        console.error('Failed to load preparation:', error);
        toast.error('Failed to load appointment preparation');
        navigate('/medical');
      } finally {
        setLoading(false);
      }
    };
    
    loadPreparation();
  }, [editId, isDemoMode, navigate]);

  // Get medical data
  const getMedications = () => {
    if (isDemoMode && demoData?.medical) {
      return demoData.medical.medications || [];
    }
    return medications || [];
  };

  const getAllergies = () => {
    if (isDemoMode && demoData?.medical) {
      return demoData.medical.allergies || [];
    }
    return allergies || [];
  };

  const getConditions = () => {
    if (isDemoMode && demoData?.medical) {
      return demoData.medical.conditions || [];
    }
    return conditions || [];
  };

  const getDoctors = () => {
    if (isDemoMode && demoData?.medical) {
      return demoData.medical.doctors || [];
    }
    return doctors || [];
  };

  // Update form data
  const updatePrepData = (field, value) => {
    setPrepData(prev => ({ ...prev, [field]: value }));
  };

  // Add a symptom
  const addSymptom = () => {
    if (!prepData.newSymptom.trim()) return;
    setPrepData(prev => ({
      ...prev,
      symptoms: [...prev.symptoms, { 
        text: prev.newSymptom.trim(), 
        startDate: prev.symptomStart,
        id: Date.now() 
      }],
      newSymptom: '',
      symptomStart: '',
    }));
  };

  // Remove a symptom
  const removeSymptom = (id) => {
    setPrepData(prev => ({
      ...prev,
      symptoms: prev.symptoms.filter(s => s.id !== id),
    }));
  };

  // Add a question
  const addQuestion = () => {
    if (!prepData.newQuestion.trim()) return;
    setPrepData(prev => ({
      ...prev,
      questions: [...prev.questions, { 
        text: prev.newQuestion.trim(), 
        answered: false,
        id: Date.now() 
      }],
      newQuestion: '',
    }));
  };

  // Remove a question
  const removeQuestion = (id) => {
    setPrepData(prev => ({
      ...prev,
      questions: prev.questions.filter(q => q.id !== id),
    }));
  };

  // Toggle question answered status
  const toggleQuestionAnswered = (id) => {
    setPrepData(prev => ({
      ...prev,
      questions: prev.questions.map(q => 
        q.id === id ? { ...q, answered: !q.answered } : q
      ),
    }));
  };

  // Toggle health info selection
  const toggleSelection = (field, id) => {
    setPrepData(prev => ({
      ...prev,
      [field]: prev[field].includes(id)
        ? prev[field].filter(i => i !== id)
        : [...prev[field], id],
    }));
  };

  // Navigate steps
  const nextStep = () => {
    if (currentStep < STEPS.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  // Prepare data for API (exclude form-only fields)
  const prepareDataForSave = () => {
    const {
      newSymptom,
      symptomStart,
      newQuestion,
      ...dataToSave
    } = prepData;
    
    // Set status to 'ready' when saving from the final step
    // Only if minimum required fields are filled
    const hasDoctor = dataToSave.doctorName || dataToSave.doctorId;
    const hasDate = dataToSave.appointmentDate;
    
    if (hasDoctor && hasDate) {
      dataToSave.status = 'ready';
    }
    
    return dataToSave;
  };

  // Save preparation
  const savePreparation = async () => {
    // Prevent duplicate submissions
    if (hasSubmitted || saving) {
      return;
    }

    if (isDemoMode) {
      toast.success('Appointment preparation saved (demo)');
      navigate('/medical');
      return;
    }

    setSaving(true);
    setHasSubmitted(true);
    
    try {
      const dataToSave = prepareDataForSave();
      
      if (isEditing && prepData.id) {
        // Update existing preparation
        await API.put(`/medical/appointment-preps/${prepData.id}`, dataToSave);
        toast.success('Appointment preparation updated');
      } else {
        // Create new preparation
        await API.post('/medical/appointment-prep', dataToSave);
        toast.success('Appointment preparation saved');
      }
      navigate('/medical');
    } catch (error) {
      console.error('Failed to save:', error);
      const errorMessage = error.response?.data?.detail || 'Failed to save preparation';
      toast.error(errorMessage);
      setHasSubmitted(false); // Allow retry on error
    } finally {
      setSaving(false);
    }
  };

  // Delete preparation
  const deletePreparation = async () => {
    if (!isEditing || !prepData.id) return;
    
    if (isDemoMode) {
      toast.success('Appointment preparation deleted (demo)');
      navigate('/medical');
      return;
    }

    setDeleting(true);
    try {
      await API.delete(`/medical/appointment-preps/${prepData.id}`);
      toast.success('Appointment preparation deleted');
      navigate('/medical');
    } catch (error) {
      console.error('Failed to delete:', error);
      const errorMessage = error.response?.data?.detail || 'Failed to delete preparation';
      toast.error(errorMessage);
    } finally {
      setDeleting(false);
    }
  };

  // Toggle section expansion
  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  // Render step content
  const renderStepContent = () => {
    switch (STEPS[currentStep].id) {
      case 'basics':
        return (
          <div className="space-y-4">
            <div>
              <Label htmlFor="doctor">Doctor / Provider</Label>
              <Select
                value={prepData.doctorId}
                onValueChange={(value) => {
                  const doc = getDoctors().find(d => d.id === value);
                  updatePrepData('doctorId', value);
                  if (doc) {
                    updatePrepData('doctorName', doc.name);
                    updatePrepData('specialty', doc.specialty || '');
                  }
                }}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Select a doctor" />
                </SelectTrigger>
                <SelectContent>
                  {getDoctors().map(doc => (
                    <SelectItem key={doc.id} value={doc.id}>
                      {doc.name} {doc.specialty && `(${doc.specialty})`}
                    </SelectItem>
                  ))}
                  <SelectItem value="other">Other / New Doctor</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {prepData.doctorId === 'other' && (
              <div className="space-y-3">
                <div>
                  <Label htmlFor="doctorName">Doctor Name</Label>
                  <Input
                    id="doctorName"
                    value={prepData.doctorName}
                    onChange={(e) => updatePrepData('doctorName', e.target.value)}
                    placeholder="Dr. Smith"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="specialty">Specialty</Label>
                  <Input
                    id="specialty"
                    value={prepData.specialty}
                    onChange={(e) => updatePrepData('specialty', e.target.value)}
                    placeholder="e.g., Primary Care, Cardiology"
                    className="mt-1"
                  />
                </div>
              </div>
            )}

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="date">Appointment Date</Label>
                <Input
                  id="date"
                  type="date"
                  value={prepData.appointmentDate}
                  onChange={(e) => updatePrepData('appointmentDate', e.target.value)}
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="time">Time (optional)</Label>
                <Input
                  id="time"
                  type="time"
                  value={prepData.appointmentTime}
                  onChange={(e) => updatePrepData('appointmentTime', e.target.value)}
                  className="mt-1"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="reason">Reason for Visit</Label>
              <Textarea
                id="reason"
                value={prepData.reason}
                onChange={(e) => updatePrepData('reason', e.target.value)}
                placeholder="What brings you to this appointment?"
                className="mt-1"
                rows={3}
              />
            </div>
          </div>
        );

      case 'concerns':
        return (
          <div className="space-y-4">
            <div>
              <Label>Current Symptoms</Label>
              <p className="text-sm text-slate-500 mb-2">What symptoms are you experiencing?</p>
              
              <div className="flex gap-2 mb-2">
                <Input
                  value={prepData.newSymptom}
                  onChange={(e) => updatePrepData('newSymptom', e.target.value)}
                  placeholder="Describe a symptom"
                  onKeyPress={(e) => e.key === 'Enter' && addSymptom()}
                />
                <Input
                  type="date"
                  value={prepData.symptomStart}
                  onChange={(e) => updatePrepData('symptomStart', e.target.value)}
                  className="w-40"
                  placeholder="When started"
                />
                <Button onClick={addSymptom} size="sm" variant="outline">
                  <Plus className="h-4 w-4" />
                </Button>
              </div>

              {prepData.symptoms.length > 0 && (
                <div className="space-y-2 mt-3">
                  {prepData.symptoms.map(symptom => (
                    <div key={symptom.id} className="flex items-center justify-between p-2 bg-orange-50 rounded-lg">
                      <div>
                        <span className="text-sm">{symptom.text}</span>
                        {symptom.startDate && (
                          <span className="text-xs text-slate-500 ml-2">
                            (since {format(new Date(symptom.startDate), 'MMM d')})
                          </span>
                        )}
                      </div>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => removeSymptom(symptom.id)}
                      >
                        <Trash2 className="h-4 w-4 text-red-500" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div>
              <Label htmlFor="changes">Changes Since Last Visit</Label>
              <Textarea
                id="changes"
                value={prepData.changesSinceLastVisit}
                onChange={(e) => updatePrepData('changesSinceLastVisit', e.target.value)}
                placeholder="Any changes in your health, lifestyle, or circumstances since your last appointment?"
                className="mt-1"
                rows={4}
              />
            </div>
          </div>
        );

      case 'questions':
        return (
          <div className="space-y-4">
            <div>
              <Label>Questions for Your Doctor</Label>
              <p className="text-sm text-slate-500 mb-2">
                Write down questions so you don&apos;t forget to ask
              </p>
              
              <div className="flex gap-2 mb-2">
                <Input
                  value={prepData.newQuestion}
                  onChange={(e) => updatePrepData('newQuestion', e.target.value)}
                  placeholder="What do you want to ask?"
                  onKeyPress={(e) => e.key === 'Enter' && addQuestion()}
                />
                <Button onClick={addQuestion} size="sm" variant="outline">
                  <Plus className="h-4 w-4" />
                </Button>
              </div>

              {prepData.questions.length > 0 && (
                <div className="space-y-2 mt-3">
                  {prepData.questions.map(question => (
                    <div 
                      key={question.id} 
                      className={`flex items-center gap-3 p-3 rounded-lg ${
                        question.answered ? 'bg-green-50' : 'bg-blue-50'
                      }`}
                    >
                      <button
                        onClick={() => toggleQuestionAnswered(question.id)}
                        className="flex-shrink-0"
                      >
                        {question.answered ? (
                          <CheckCircle2 className="h-5 w-5 text-green-600" />
                        ) : (
                          <Circle className="h-5 w-5 text-blue-400" />
                        )}
                      </button>
                      <span className={`flex-1 text-sm ${question.answered ? 'line-through text-slate-500' : ''}`}>
                        {question.text}
                      </span>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => removeQuestion(question.id)}
                      >
                        <Trash2 className="h-4 w-4 text-red-500" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}

              {prepData.questions.length === 0 && (
                <div className="text-center py-6 bg-slate-50 rounded-lg mt-3">
                  <MessageSquareText className="h-8 w-8 text-slate-300 mx-auto mb-2" />
                  <p className="text-sm text-slate-500">No questions added yet</p>
                </div>
              )}
            </div>
          </div>
        );

      case 'health':
        return (
          <div className="space-y-4">
            <p className="text-sm text-slate-600">
              Select relevant items to include in your appointment summary
            </p>

            {/* Medications */}
            <Card>
              <CardHeader 
                className="p-3 cursor-pointer"
                onClick={() => toggleSection('medications')}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Pill className="h-4 w-4 text-purple-600" />
                    <span className="font-medium text-sm">Medications</span>
                    {prepData.selectedMedications.length > 0 && (
                      <Badge variant="secondary" className="text-xs">
                        {prepData.selectedMedications.length} selected
                      </Badge>
                    )}
                  </div>
                  {expandedSections.medications ? (
                    <ChevronUp className="h-4 w-4 text-slate-400" />
                  ) : (
                    <ChevronDown className="h-4 w-4 text-slate-400" />
                  )}
                </div>
              </CardHeader>
              {expandedSections.medications && (
                <CardContent className="p-3 pt-0">
                  <div className="space-y-2 max-h-40 overflow-y-auto">
                    {getMedications().map(med => (
                      <label key={med.id} className="flex items-center gap-2 p-2 rounded hover:bg-slate-50 cursor-pointer">
                        <Checkbox
                          checked={prepData.selectedMedications.includes(med.id)}
                          onCheckedChange={() => toggleSelection('selectedMedications', med.id)}
                        />
                        <span className="text-sm">{med.name} {med.dosage && `(${med.dosage})`}</span>
                      </label>
                    ))}
                    {getMedications().length === 0 && (
                      <p className="text-sm text-slate-500 py-2">No medications recorded</p>
                    )}
                  </div>
                </CardContent>
              )}
            </Card>

            {/* Allergies */}
            <Card>
              <CardHeader 
                className="p-3 cursor-pointer"
                onClick={() => toggleSection('allergies')}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <AlertTriangle className="h-4 w-4 text-red-600" />
                    <span className="font-medium text-sm">Allergies</span>
                    {prepData.selectedAllergies.length > 0 && (
                      <Badge variant="secondary" className="text-xs">
                        {prepData.selectedAllergies.length} selected
                      </Badge>
                    )}
                  </div>
                  {expandedSections.allergies ? (
                    <ChevronUp className="h-4 w-4 text-slate-400" />
                  ) : (
                    <ChevronDown className="h-4 w-4 text-slate-400" />
                  )}
                </div>
              </CardHeader>
              {expandedSections.allergies && (
                <CardContent className="p-3 pt-0">
                  <div className="space-y-2 max-h-40 overflow-y-auto">
                    {getAllergies().map(allergy => (
                      <label key={allergy.id} className="flex items-center gap-2 p-2 rounded hover:bg-slate-50 cursor-pointer">
                        <Checkbox
                          checked={prepData.selectedAllergies.includes(allergy.id)}
                          onCheckedChange={() => toggleSelection('selectedAllergies', allergy.id)}
                        />
                        <span className="text-sm">{allergy.name}</span>
                        {allergy.severity && (
                          <Badge variant="outline" className="text-xs">{allergy.severity}</Badge>
                        )}
                      </label>
                    ))}
                    {getAllergies().length === 0 && (
                      <p className="text-sm text-slate-500 py-2">No allergies recorded</p>
                    )}
                  </div>
                </CardContent>
              )}
            </Card>

            {/* Conditions */}
            <Card>
              <CardHeader 
                className="p-3 cursor-pointer"
                onClick={() => toggleSection('conditions')}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Activity className="h-4 w-4 text-amber-600" />
                    <span className="font-medium text-sm">Medical Conditions</span>
                    {prepData.selectedConditions.length > 0 && (
                      <Badge variant="secondary" className="text-xs">
                        {prepData.selectedConditions.length} selected
                      </Badge>
                    )}
                  </div>
                  {expandedSections.conditions ? (
                    <ChevronUp className="h-4 w-4 text-slate-400" />
                  ) : (
                    <ChevronDown className="h-4 w-4 text-slate-400" />
                  )}
                </div>
              </CardHeader>
              {expandedSections.conditions && (
                <CardContent className="p-3 pt-0">
                  <div className="space-y-2 max-h-40 overflow-y-auto">
                    {getConditions().map(condition => (
                      <label key={condition.id} className="flex items-center gap-2 p-2 rounded hover:bg-slate-50 cursor-pointer">
                        <Checkbox
                          checked={prepData.selectedConditions.includes(condition.id)}
                          onCheckedChange={() => toggleSelection('selectedConditions', condition.id)}
                        />
                        <span className="text-sm">{condition.name}</span>
                      </label>
                    ))}
                    {getConditions().length === 0 && (
                      <p className="text-sm text-slate-500 py-2">No conditions recorded</p>
                    )}
                  </div>
                </CardContent>
              )}
            </Card>
          </div>
        );

      case 'summary':
        return (
          <div className="space-y-4">
            <Card className="bg-emerald-50 border-emerald-200">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Sparkles className="h-5 w-5 text-emerald-600" />
                  Appointment Summary
                </CardTitle>
                <CardDescription>
                  Review your preparation before your appointment
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Appointment Info */}
                <div>
                  <h4 className="text-sm font-semibold text-slate-700 mb-1">Appointment</h4>
                  <p className="text-sm">
                    {prepData.doctorName || 'Doctor not specified'} 
                    {prepData.specialty && ` (${prepData.specialty})`}
                  </p>
                  <p className="text-sm text-slate-600">
                    {prepData.appointmentDate && format(new Date(prepData.appointmentDate), 'MMMM d, yyyy')}
                    {prepData.appointmentTime && ` at ${prepData.appointmentTime}`}
                  </p>
                  {prepData.reason && (
                    <p className="text-sm text-slate-600 mt-1">
                      <strong>Reason:</strong> {prepData.reason}
                    </p>
                  )}
                </div>

                {/* Symptoms */}
                {prepData.symptoms.length > 0 && (
                  <div>
                    <h4 className="text-sm font-semibold text-slate-700 mb-1">Symptoms</h4>
                    <ul className="text-sm space-y-1">
                      {prepData.symptoms.map(s => (
                        <li key={s.id} className="flex items-center gap-2">
                          <Thermometer className="h-3 w-3 text-orange-500" />
                          {s.text}
                          {s.startDate && ` (since ${format(new Date(s.startDate), 'MMM d')})`}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Questions */}
                {prepData.questions.length > 0 && (
                  <div>
                    <h4 className="text-sm font-semibold text-slate-700 mb-1">Questions to Ask</h4>
                    <ul className="text-sm space-y-1">
                      {prepData.questions.map(q => (
                        <li key={q.id} className="flex items-center gap-2">
                          {q.answered ? (
                            <CheckCircle2 className="h-3 w-3 text-green-500" />
                          ) : (
                            <Circle className="h-3 w-3 text-blue-400" />
                          )}
                          <span className={q.answered ? 'line-through text-slate-500' : ''}>
                            {q.text}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Selected Health Info */}
                {(prepData.selectedMedications.length > 0 || 
                  prepData.selectedAllergies.length > 0 || 
                  prepData.selectedConditions.length > 0) && (
                  <div>
                    <h4 className="text-sm font-semibold text-slate-700 mb-1">Health Information</h4>
                    <div className="flex flex-wrap gap-1">
                      {prepData.selectedMedications.map(id => {
                        const med = getMedications().find(m => m.id === id);
                        return med && (
                          <Badge key={id} variant="outline" className="text-xs bg-purple-50">
                            <Pill className="h-3 w-3 mr-1" />
                            {med.name}
                          </Badge>
                        );
                      })}
                      {prepData.selectedAllergies.map(id => {
                        const allergy = getAllergies().find(a => a.id === id);
                        return allergy && (
                          <Badge key={id} variant="outline" className="text-xs bg-red-50">
                            <AlertTriangle className="h-3 w-3 mr-1" />
                            {allergy.name}
                          </Badge>
                        );
                      })}
                      {prepData.selectedConditions.map(id => {
                        const condition = getConditions().find(c => c.id === id);
                        return condition && (
                          <Badge key={id} variant="outline" className="text-xs bg-amber-50">
                            <Activity className="h-3 w-3 mr-1" />
                            {condition.name}
                          </Badge>
                        );
                      })}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Follow-up Notes */}
            <div>
              <Label htmlFor="followUp">Follow-up Notes (add during/after appointment)</Label>
              <Textarea
                id="followUp"
                value={prepData.followUpNotes}
                onChange={(e) => updatePrepData('followUpNotes', e.target.value)}
                placeholder="What did the doctor say? Any instructions?"
                className="mt-1"
                rows={3}
              />
            </div>

            <div>
              <Label htmlFor="nextSteps">Next Steps</Label>
              <Textarea
                id="nextSteps"
                value={prepData.nextSteps}
                onChange={(e) => updatePrepData('nextSteps', e.target.value)}
                placeholder="Follow-up appointments, tests to schedule, etc."
                className="mt-1"
                rows={2}
              />
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  // Show loading spinner while fetching existing prep
  if (loading) {
    return (
      <div 
        className="min-h-screen pb-28 flex items-center justify-center"
        style={{ background: THEME.bgGradient }}
      >
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-emerald-600 mx-auto mb-2" />
          <p className="text-slate-600">Loading preparation...</p>
        </div>
      </div>
    );
  }

  return (
    <div 
      className="min-h-screen pb-28"
      style={{ background: THEME.bgGradient }}
      data-testid="appointment-prep-page"
    >
      {/* Header */}
      <header className="sticky top-0 z-40 bg-white/80 backdrop-blur-md border-b border-emerald-100">
        <div className="max-w-2xl mx-auto px-4 py-3">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/medical')}
              className="p-2 -ml-2"
            >
              <ArrowLeft className="h-5 w-5 text-slate-600" />
            </Button>
            <div className="flex-1">
              <h1 className="text-lg font-semibold text-slate-800">
                {isEditing ? 'Edit Appointment Prep' : 'Prepare for Appointment'}
              </h1>
              <p className="text-xs text-slate-500">
                Step {currentStep + 1} of {STEPS.length}: {STEPS[currentStep].title}
              </p>
            </div>
            
            {/* Delete button for editing mode */}
            {isEditing && prepData.id && (
              <Button
                variant="ghost"
                size="sm"
                onClick={deletePreparation}
                disabled={deleting}
                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                data-testid="appointment-prep-delete"
              >
                {deleting ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Trash2 className="h-4 w-4" />
                )}
              </Button>
            )}
          </div>
        </div>

        {/* Progress Indicator */}
        <div className="max-w-2xl mx-auto px-4 pb-3">
          <div className="flex gap-1">
            {STEPS.map((step, index) => (
              <div
                key={step.id}
                className={`h-1 flex-1 rounded-full transition-colors ${
                  index <= currentStep ? 'bg-emerald-500' : 'bg-slate-200'
                }`}
              />
            ))}
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-2xl mx-auto px-4 py-6">
        <Card className="shadow-sm">
          <CardHeader className="pb-3">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                {(() => {
                  const StepIcon = STEPS[currentStep].icon;
                  return <StepIcon className="h-5 w-5 text-emerald-600" />;
                })()}
              </div>
              <div>
                <CardTitle className="text-lg">{STEPS[currentStep].title}</CardTitle>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {renderStepContent()}
          </CardContent>
        </Card>

        {/* Navigation Buttons */}
        <div className="flex justify-between mt-6">
          <Button
            variant="outline"
            onClick={prevStep}
            disabled={currentStep === 0}
            data-testid="appointment-prep-back"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>

          {currentStep === STEPS.length - 1 ? (
            <Button
              onClick={savePreparation}
              disabled={saving || hasSubmitted}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="appointment-prep-save"
            >
              {saving ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Save className="h-4 w-4 mr-2" />
              )}
              {isEditing ? 'Update Preparation' : 'Save Appointment Preparation'}
            </Button>
          ) : (
            <Button 
              onClick={nextStep} 
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="appointment-prep-next"
            >
              Next
              <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          )}
        </div>
      </main>
    </div>
  );
};

export default AppointmentPrepPage;
